import * as React from 'react';
import { Dispatch, SetStateAction } from 'react';
import { DomainTypeGq } from '../../graphql/projects/models/DomainTypeGq';
import { ProjectGq } from '../../graphql/projects/models/ProjectGq';
import { AvailableAnalysisDataGq } from '../../graphql/projects/queries/analysis/useGetAvailableAnalysisDataGq';
import BestKeywordsCompetitors from './Charts/CompetitiveInsights/BestCompetitorKeywords/BestCompetitorKeywords';
import CompetitiveIntelligence from './Charts/CompetitiveInsights/CompetitiveIntelligence/CompetitiveIntelligence';
import CompetitiveLandscape from './Charts/CompetitiveInsights/CompetitiveLandscape';
import PerformanceCompetitors from './Charts/CompetitiveInsights/PerformanceCompetitors/PerformanceCompetitors';
import RankingHeatMap from './Charts/CompetitiveInsights/RankingHeatmap';
import MarketDemand from './Charts/MarketDemand/MarketDemand/MarketDemand';
import MarketTrendChart from './Charts/MarketDemand/MarketTrend';
import MonthlyDemandChart from './Charts/MarketDemand/MonthlyDemand';
import SearchVolumeDistributionPieChart from './Charts/MarketDemand/SearchVolumeDistribution/SearchVolumeDistributionPieChart';
import HBLtChartByTopic from './Charts/SearchBehaviour/HeadBodyLongTail/HBLtChartByTopic';
import HBLtPieChart from './Charts/SearchBehaviour/HeadBodyLongTail/HBLtPieChart';
import TagGroupsAndKeywords from './Charts/SearchBehaviour/IndustryInsights/TagGroupsAndKeywords';
import TagGroupsAndTags from './Charts/SearchBehaviour/IndustryInsights/TagGroupsAndTags';
import TagsAndKeywords from './Charts/SearchBehaviour/IndustryInsights/TagsAndKeywords';
import TopCombinations from './Charts/SearchBehaviour/TopCombinations/TopCombinations';
import TopicalDemand from './Charts/SearchBehaviour/TopicalDemand';
import TopTags from './Charts/SearchBehaviour/TopTags';
import BestTrafficPages from './Charts/SiteInsights/BestTrafficPages';
import PerformanceChart from './Charts/SiteInsights/Performance/Performance';
import RanksByKeywordsChart from './Charts/SiteInsights/RanksByKeywordsChart';
import RanksByTrafficChart from './Charts/SiteInsights/RanksByTrafficChart';
import RanksByVolumeChart from './Charts/SiteInsights/RanksByVolumeChart';
import TopicRanksByKeywordsChart from './Charts/SiteInsights/TopicRanksByKeywords/TopicRanksByKeywordsChart';
import RanksPerGroupTrafficChart from './Charts/SiteInsights/TopicRanksByTraffic/RanksPerGroupTrafficChart';
import RanksPerGroupVolumeChart from './Charts/SiteInsights/TopicRanksByVolume/RanksPerGroupVolumeChart';
import TopKeywordsByRank from './Charts/SiteInsights/TopKeywords/TopKeywordsByRank';
import TopKeywordsByVolume from './Charts/SiteInsights/TopKeywords/TopKeywordsByVolume';
import ImprovedRanking from './Charts/SitePotential/ImprovedRanking';
import MarketPotential from './Charts/SitePotential/MarketPotential';
import MarketTrafficCost from './Charts/SitePotential/MarketTrafficCost';
import RevenueForecastChart from './Charts/SitePotential/RevenueForecast';
import TopicalCoverage from './Charts/SitePotential/TopicalCoverage';
import TrafficForecastChart from './Charts/SitePotential/TrafficForecast';
import { AnalysisFilterState } from './Filter/analysisFilterState';
import { internationalText, Language } from './internationalText';
import { ColorPalette } from './Palette/ColorPalette';

export interface AnalysisListItem {
  downloadId: string;
  menuName: string;
  title?: string;
  subtitle?: string;
  footnote?: string | React.ReactNode;
  chart?: React.ComponentType<ChartProps>;
  disabled: boolean;
  clientDomainRequired?: boolean;
  analysisList?: AnalysisListItem[];
  wideChart?: boolean;
}

export interface ChartProps {
  project: ProjectGq;
  analysisFilterState?: AnalysisFilterState;
  setAnalysisFilterState: Dispatch<SetStateAction<AnalysisFilterState>>;
  title?: string;
  subtitle?: string;
  wideChart?: boolean;
  language: Language;
  palette: ColorPalette;
  skipAnimation?: boolean;
  emptyOnFail?: boolean;
}

export function createAnalysisList(
  project: ProjectGq,
  data: AvailableAnalysisDataGq,
  language: Language
): AnalysisListItem[] {
  const keywordData = data?.keywordData;
  const taggedData = data?.taggedData;
  const serpData = data?.serpData;
  const monthlyData = data?.monthlyData;
  const noClientDomain = !project.domains?.some((d) => d.type === DomainTypeGq.PROJECT);
  return [
    {
      downloadId: '',
      menuName: internationalText.menu.market_demand[language],
      disabled: false,
      analysisList: [
        {
          downloadId: 'Market demand',
          menuName: internationalText.chart.market_demand.title[language],
          title: internationalText.chart.market_demand.title[language],
          subtitle: internationalText.chart.market_demand.subtitle[language],
          chart: MarketDemand,
          disabled: !keywordData,
        },
        {
          downloadId: 'Demand by month',
          menuName: internationalText.chart.demand_by_month.title[language],
          title: internationalText.chart.demand_by_month.title[language],
          subtitle: internationalText.chart.demand_by_month.subtitle[language],
          chart: MonthlyDemandChart,
          disabled: !(keywordData && monthlyData),
        },
        {
          downloadId: 'Market trend',
          menuName: internationalText.chart.demand_trend.title[language],
          title: internationalText.chart.demand_trend.title[language],
          subtitle: internationalText.chart.demand_trend.subtitle[language],
          chart: MarketTrendChart,
          disabled: !(keywordData && monthlyData) || project.longMonthDisabled,
        },
        {
          downloadId: 'Search volume distribution',
          menuName: internationalText.chart.search_volume_distribution.title[language],
          title: internationalText.chart.search_volume_distribution.title[language],
          subtitle: internationalText.chart.search_volume_distribution.subtitle[language],
          chart: SearchVolumeDistributionPieChart,
          disabled: !keywordData,
        },
      ],
    },
    {
      downloadId: '',
      menuName: internationalText.menu.search_behaviour[language],
      disabled: false,
      analysisList: [
        {
          downloadId: 'Demand by topic',
          menuName: internationalText.chart.demand_by_topic.title[language],
          title: internationalText.chart.demand_by_topic.title[language],
          subtitle: internationalText.chart.demand_by_topic.subtitle[language],
          footnote: internationalText.chart.demand_by_topic.footnote[language],
          chart: TopicalDemand,
          disabled: !(keywordData && taggedData),
        },
        {
          downloadId: 'Topic combinations',
          menuName: internationalText.chart.topic_combinations.title[language],
          title: internationalText.chart.topic_combinations.title[language],
          subtitle: internationalText.chart.topic_combinations.subtitle[language],
          chart: TopCombinations,
          disabled: !(keywordData && taggedData && project.tagGroups.length >= 2),
        },
        {
          downloadId: 'Demand by tag',
          menuName: internationalText.chart.keyword_cluster_by_demand.title[language],
          title: internationalText.chart.keyword_cluster_by_demand.title[language],
          subtitle: internationalText.chart.keyword_cluster_by_demand.subtitle[language],
          footnote: internationalText.chart.keyword_cluster_by_demand.footnote[language],
          chart: TopTags,
          disabled: !(keywordData && taggedData),
        },
        {
          downloadId: 'Tag groups and keywords',
          menuName: internationalText.chart.topic_keyword_demand.title[language],
          title: internationalText.chart.topic_keyword_demand.title[language],
          subtitle: internationalText.chart.topic_keyword_demand.subtitle[language],
          footnote: internationalText.chart.topic_keyword_demand.footnote[language],
          chart: TagGroupsAndKeywords,
          disabled: !(keywordData && taggedData),
        },
        {
          downloadId: 'Tag groups and tags',
          menuName: internationalText.chart.sub_topic_demand.title[language],
          title: internationalText.chart.sub_topic_demand.title[language],
          subtitle: internationalText.chart.sub_topic_demand.subtitle[language],
          footnote: internationalText.chart.sub_topic_demand.footnote[language],
          chart: TagGroupsAndTags,
          disabled: !(keywordData && taggedData),
        },
        {
          downloadId: 'Tags and keywords',
          menuName: internationalText.chart.sub_topic_keyword_demand.title[language],
          title: internationalText.chart.sub_topic_keyword_demand.title[language],
          subtitle: internationalText.chart.sub_topic_keyword_demand.subtitle[language],
          footnote: internationalText.chart.sub_topic_keyword_demand.footnote[language],
          chart: TagsAndKeywords,
          disabled: !(keywordData && taggedData),
        },
        {
          downloadId: 'Search term specificity',
          menuName: internationalText.chart.keyword_specificity.title[language],
          title: internationalText.chart.keyword_specificity.title[language],
          subtitle: internationalText.chart.keyword_specificity.subtitle[language],
          footnote: internationalText.chart.keyword_specificity.footnote[language],
          chart: HBLtPieChart,
          disabled: !keywordData,
        },
        {
          downloadId: 'Specificity by topic',
          menuName: internationalText.chart.keyword_specificity_by_topic.title[language],
          title: internationalText.chart.keyword_specificity_by_topic.title[language],
          subtitle: internationalText.chart.keyword_specificity_by_topic.subtitle[language],
          footnote: internationalText.chart.keyword_specificity_by_topic.footnote[language],
          chart: HBLtChartByTopic,
          disabled: !(keywordData && taggedData),
        },
      ],
    },
    {
      downloadId: '',
      menuName: internationalText.menu.client_insights[language],
      disabled: false,
      analysisList: [
        {
          downloadId: 'Keywords by rank',
          menuName: internationalText.chart.ranking_distribution.title[language],
          title: internationalText.chart.ranking_distribution.title[language],
          subtitle: internationalText.chart.ranking_distribution.subtitle[language],
          chart: RanksByKeywordsChart,
          disabled: !(keywordData && serpData),
          clientDomainRequired: noClientDomain,
        },
        {
          downloadId: 'Demand by rank',
          menuName: internationalText.chart.demand_ranking_distribution.title[language],
          title: internationalText.chart.demand_ranking_distribution.title[language],
          subtitle: internationalText.chart.demand_ranking_distribution.subtitle[language],
          chart: RanksByVolumeChart,
          disabled: !(keywordData && serpData),
          clientDomainRequired: noClientDomain,
        },
        {
          downloadId: 'Traffic by rank',
          menuName: internationalText.chart.traffic_ranking_distribution.title[language],
          title: internationalText.chart.traffic_ranking_distribution.title[language],
          subtitle: internationalText.chart.traffic_ranking_distribution.subtitle[language],
          chart: RanksByTrafficChart,
          disabled: !(keywordData && serpData),
          clientDomainRequired: noClientDomain,
        },
        {
          downloadId: 'Topic keywords by rank',
          menuName: internationalText.chart.topic_ranking_distribution.title[language],
          title: internationalText.chart.topic_ranking_distribution.title[language],
          subtitle: internationalText.chart.topic_ranking_distribution.subtitle[language],
          chart: TopicRanksByKeywordsChart,
          disabled: !(keywordData && taggedData && serpData),
          clientDomainRequired: noClientDomain,
        },
        {
          downloadId: 'Topic demand by rank',
          menuName: internationalText.chart.topic_demand_ranking_distribution.title[language],
          title: internationalText.chart.topic_demand_ranking_distribution.title[language],
          subtitle: internationalText.chart.topic_demand_ranking_distribution.subtitle[language],
          chart: RanksPerGroupVolumeChart,
          disabled: !(keywordData && taggedData && serpData),
          clientDomainRequired: noClientDomain,
        },
        {
          downloadId: 'Topic traffic by rank',
          menuName: internationalText.chart.topic_traffic_ranking_distribution.title[language],
          title: internationalText.chart.topic_traffic_ranking_distribution.title[language],
          subtitle: internationalText.chart.topic_traffic_ranking_distribution.subtitle[language],
          chart: RanksPerGroupTrafficChart,
          disabled: !(keywordData && taggedData && serpData),
          clientDomainRequired: noClientDomain,
        },
        {
          downloadId: 'Top volume keywords',
          menuName: internationalText.chart.top_volume_keywords.title[language],
          title: internationalText.chart.top_volume_keywords.title[language],
          subtitle: internationalText.chart.top_volume_keywords.subtitle[language],
          chart: TopKeywordsByVolume,
          disabled: !(keywordData && taggedData),
          wideChart: true,
          clientDomainRequired: noClientDomain,
        },
        {
          downloadId: 'Top volume keywords rank',
          menuName: internationalText.chart.top_volume_keywords_and_rank.title[language],
          title: internationalText.chart.top_volume_keywords_and_rank.title[language],
          subtitle: internationalText.chart.top_volume_keywords_and_rank.subtitle[language],
          chart: TopKeywordsByRank,
          disabled: !(keywordData && taggedData && serpData),
          wideChart: true,
          clientDomainRequired: noClientDomain,
        },
        {
          downloadId: 'Best traffic pages',
          menuName: internationalText.chart.best_pages_by_traffic.title[language],
          title: internationalText.chart.best_pages_by_traffic.title[language],
          subtitle: internationalText.chart.best_pages_by_traffic.subtitle[language],
          chart: BestTrafficPages,
          disabled: !(keywordData && serpData),
          clientDomainRequired: noClientDomain,
        },
        {
          downloadId: 'Performance',
          menuName: internationalText.chart.performance.title[language],
          title: internationalText.chart.performance.title[language],
          subtitle: internationalText.chart.performance.subtitle[language],
          chart: PerformanceChart,
          disabled: !(keywordData && serpData),
          clientDomainRequired: noClientDomain,
        },
      ],
    },
    {
      downloadId: '',
      menuName: internationalText.menu.client_potential[language],
      disabled: false,
      analysisList: [
        {
          downloadId: 'Market potential',
          menuName: internationalText.chart.market_potential.title[language],
          title: internationalText.chart.market_potential.title[language],
          chart: MarketPotential,
          disabled: !keywordData,
          clientDomainRequired: noClientDomain,
        },
        {
          downloadId: 'traffic_cost',
          menuName: internationalText.chart.traffic_cost.title[language],
          title: internationalText.chart.traffic_cost.title[language],
          subtitle: internationalText.chart.traffic_cost.subtitle[language],
          chart: MarketTrafficCost,
          disabled: !keywordData,
          clientDomainRequired: noClientDomain,
          footnote: internationalText.chart.traffic_cost.footnote[language],
        },
        {
          downloadId: 'Topical coverage',
          menuName: internationalText.chart.market_potential_by_topic.title[language],
          title: internationalText.chart.market_potential_by_topic.title[language],
          subtitle: internationalText.chart.market_potential_by_topic.subtitle[language],
          chart: TopicalCoverage,
          disabled: !(keywordData && taggedData && serpData),
          clientDomainRequired: noClientDomain,
        },
        {
          downloadId: 'Improved rank potential',
          menuName: internationalText.chart.market_potential_by_rank_improvement.title[language],
          title: internationalText.chart.market_potential_by_rank_improvement.title[language],
          subtitle: internationalText.chart.market_potential_by_rank_improvement.subtitle[language],
          footnote: internationalText.chart.market_potential_by_rank_improvement.footnote[language],
          chart: ImprovedRanking,
          disabled: !(keywordData && serpData),
          clientDomainRequired: noClientDomain,
        },
        {
          downloadId: 'Traffic forecast',
          menuName: internationalText.chart.traffic_forecast.title[language],
          title: internationalText.chart.traffic_forecast.title[language],
          subtitle: internationalText.chart.traffic_forecast.subtitle[language],
          footnote: internationalText.chart.traffic_forecast.footnote[language],
          chart: TrafficForecastChart,
          disabled: !(keywordData && serpData),
          clientDomainRequired: noClientDomain,
        },
        {
          downloadId: 'Revenue forecast',
          menuName: internationalText.chart.revenue_forecast.title[language],
          title: internationalText.chart.revenue_forecast.title[language],
          subtitle: internationalText.chart.revenue_forecast.subtitle[language],
          footnote: internationalText.chart.revenue_forecast.footnote[language],
          chart: RevenueForecastChart,
          disabled: !(keywordData && serpData),
          clientDomainRequired: noClientDomain,
        },
      ],
    },
    {
      downloadId: '',
      menuName: internationalText.menu.competitive_insights[language],
      disabled: false,
      analysisList: [
        {
          downloadId: 'Competitive ranking strategy',
          menuName: internationalText.chart.competitive_landscape.title[language],
          title: internationalText.chart.competitive_landscape.title[language],
          subtitle: internationalText.chart.competitive_landscape.subtitle[language],
          chart: CompetitiveLandscape,
          disabled: !(keywordData && serpData),
        },
        {
          downloadId: 'Performance vs competitors',
          menuName: internationalText.chart.performance_vs_competitors.title[language],
          title: internationalText.chart.performance_vs_competitors.title[language],
          subtitle: internationalText.chart.performance_vs_competitors.subtitle[language],

          chart: PerformanceCompetitors,
          disabled: !(keywordData && serpData),
        },
        {
          downloadId: 'Best competitor keywords',
          menuName: internationalText.chart.best_competitor_keywords.title[language],
          title: internationalText.chart.best_competitor_keywords.title[language],
          subtitle: internationalText.chart.best_competitor_keywords.subtitle[language],

          chart: BestKeywordsCompetitors,
          disabled: !(keywordData && serpData),
        },
        {
          downloadId: 'Competitive intelligence',
          menuName: internationalText.chart.competitors_by_topic.title[language],
          title: internationalText.chart.competitors_by_topic.title[language],
          subtitle: internationalText.chart.competitors_by_topic.subtitle[language],
          chart: CompetitiveIntelligence,
          disabled: !(keywordData && taggedData && serpData),
          wideChart: true,
        },
        {
          downloadId: 'Competitive heatmap',
          menuName: internationalText.chart.competitive_heatmap.title[language],
          title: internationalText.chart.competitive_heatmap.title[language],
          subtitle: internationalText.chart.competitive_heatmap.subtitle[language],

          chart: RankingHeatMap,
          disabled: !(keywordData && serpData),
          wideChart: true,
        },
      ],
    },
  ];
}
